import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBusinessInfo } from '@/hooks/useBusinessInfo';

const navLinks = [
  { labelKey: 'nav_home', href: '/' },
  { labelKey: 'footer_about', href: '/about' },
  { labelKey: 'nav_menu', href: '/menu' },
  { labelKey: 'nav_gallery', href: '/gallery' },
  { labelKey: 'nav_location', href: '/location' },
];

export default function Footer() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { info, waLink } = useBusinessInfo();

  const handleNavClick = (href: string) => {
    navigate(href);
    window.scrollTo(0, 0);
  };

  const year = new Date().getFullYear();

  const socialLinks = [
    { icon: 'ri-instagram-line', href: info.instagram, label: 'Instagram', active: !!info.instagram },
    { icon: 'ri-facebook-line', href: info.facebook, label: 'Facebook', active: !!info.facebook },
    { icon: 'ri-tiktok-line', href: info.tiktok, label: 'TikTok', active: !!info.tiktok },
    { icon: 'ri-whatsapp-line', href: waLink, label: 'WhatsApp', active: !!info.whatsapp },
  ].filter(s => s.active);

  return (
    <footer className="bg-[#0A0A0A] border-t border-white/5 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="https://static.readdy.ai/image/3aca7b4f25b4386bcee0afd7be9c4116/d8f7f8948694e3a12ee4d9db008d7937.png"
              alt="Bucara Burger"
              className="h-14 w-auto object-contain mb-4"
            />
            <p className="text-white/50 text-sm leading-relaxed mb-4">
              {t('footer_tagline')}
            </p>
            <div className="inline-flex items-center gap-1.5 bg-[#F57F25]/10 border border-[#F57F25]/20 text-[#F57F25] text-xs font-semibold px-3 py-1.5 rounded-full">
              <i className="ri-map-pin-line text-xs"></i>
              Boise, Idaho
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">{t('footer_nav_title')}</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <button
                    onClick={() => handleNavClick(link.href)}
                    className="text-white/50 hover:text-[#F57F25] text-sm transition-colors cursor-pointer bg-transparent border-none text-left"
                  >
                    {t(link.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">{t('footer_social_title')}</h4>
            <div className="flex flex-col gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/50 hover:text-[#F57F25] text-sm transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${s.icon} text-base`}></i>
                  </div>
                  {s.label}
                </a>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">{t('footer_contact_title')}</h4>
            <div className="space-y-3">
              {info.phone && (
                <a href={`tel:${info.phone.replace(/\D/g, '')}`} className="flex items-center gap-3 text-white/50 hover:text-[#F57F25] text-sm transition-colors cursor-pointer">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-base"></i>
                  </div>
                  {info.phone}
                </a>
              )}
              {info.whatsapp && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/50 hover:text-[#F57F25] text-sm transition-colors cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-base"></i>
                  </div>
                  {t('footer_whatsapp_order')}
                </a>
              )}
              <div className="flex items-start gap-3 text-white/50 text-sm">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-base"></i>
                </div>
                {info.address || 'Boise, Idaho 83701'}
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs text-center sm:text-left">
            {t('footer_copyright').replace('{year}', String(year))}
          </p>
          <div className="flex items-center gap-4">
            <button onClick={() => handleNavClick('/admin')} className="text-white/20 hover:text-white/40 text-xs transition-colors bg-transparent border-none cursor-pointer">Admin</button>
            <p className="text-white/20 text-xs">
              {t('footer_serving')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
